/**
 * ConnectionStatus - Real-time API/Database connectivity indicator
 * Shows green (connected), yellow (slow/degraded), red (disconnected)
 */
import { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff, AlertTriangle, Database, Server, RefreshCw } from 'lucide-react';
import { api } from '@/utils/apiClient';

export default function ConnectionStatus({ compact = false }) {
  const [status, setStatus] = useState('checking'); // checking, healthy, degraded, unhealthy
  const [details, setDetails] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const checkHealth = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const startTime = Date.now();
      const res = await api.get('/health/detailed', { timeout: 10000 });
      const latency = Date.now() - startTime;
      
      const data = res.data;
      setDetails({
        ...data,
        clientLatency: latency
      });
      
      // Determine status based on latency and server response
      if (data.status === 'healthy' && latency < 500) {
        setStatus('healthy');
      } else if (data.status === 'degraded' || latency >= 500) {
        setStatus('degraded');
      } else {
        setStatus('unhealthy');
      }
    } catch (err) {
      setStatus('unhealthy');
      setDetails({ 
        error: err.code === 'ECONNABORTED' ? 'Request timed out' : err.message,
        clientLatency: null 
      });
    }
    setLastCheck(new Date());
    setIsRefreshing(false);
  }, []);

  useEffect(() => {
    checkHealth();
    // Check every 30 seconds
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, [checkHealth]);

  const getStatusConfig = () => {
    switch (status) {
      case 'healthy':
        return {
          icon: Wifi,
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
          borderColor: 'border-green-500/30',
          pulseColor: 'bg-green-500',
          label: 'Connected',
          description: 'All systems operational'
        };
      case 'degraded':
        return {
          icon: AlertTriangle,
          color: 'text-amber-500',
          bgColor: 'bg-amber-500/10',
          borderColor: 'border-amber-500/30',
          pulseColor: 'bg-amber-500',
          label: 'Slow',
          description: 'Connection is slow or degraded'
        };
      case 'unhealthy':
        return {
          icon: WifiOff,
          color: 'text-red-500',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/30',
          pulseColor: 'bg-red-500',
          label: 'Disconnected',
          description: 'Unable to reach server'
        };
      default:
        return {
          icon: Wifi,
          color: 'text-muted-foreground',
          bgColor: 'bg-muted/30',
          borderColor: 'border-border',
          pulseColor: 'bg-muted-foreground',
          label: 'Checking...',
          description: 'Checking connection'
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  if (compact) {
    return (
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`relative flex items-center gap-2 px-3 py-2 rounded-lg ${config.bgColor} border ${config.borderColor} transition-all hover:scale-105`}
        title={`${config.label}: ${config.description}`}
        data-testid="connection-status-compact"
      >
        {/* Animated pulse dot */}
        <span className="relative flex h-2 w-2">
          {status === 'healthy' && (
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${config.pulseColor} opacity-75`}></span>
          )}
          <span className={`relative inline-flex rounded-full h-2 w-2 ${config.pulseColor}`}></span>
        </span>
        <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
        {isRefreshing && <RefreshCw className="w-3 h-3 animate-spin text-muted-foreground" />}
      </button>
    );
  }

  return (
    <div className="space-y-2" data-testid="connection-status">
      {/* Main Status Button */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`w-full flex items-center gap-3 p-3 rounded-xl ${config.bgColor} border ${config.borderColor} transition-all hover:scale-[1.02]`}
        data-testid="connection-status-btn"
      >
        {/* Status Icon with pulse */}
        <div className="relative">
          <Icon className={`w-4 h-4 ${config.color}`} />
          {status === 'healthy' && (
            <span className={`absolute -top-0.5 -right-0.5 flex h-2 w-2`}>
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${config.pulseColor} opacity-75`}></span>
              <span className={`relative inline-flex rounded-full h-2 w-2 ${config.pulseColor}`}></span>
            </span>
          )}
        </div>
        
        <div className="flex-1 text-left">
          <div className={`text-xs font-medium ${config.color}`}>{config.label}</div>
          <div className="text-[10px] text-muted-foreground">{config.description}</div>
        </div>
        
        {isRefreshing ? (
          <RefreshCw className="w-3 h-3 animate-spin text-muted-foreground" />
        ) : (
          <span
            onClick={(e) => { e.stopPropagation(); checkHealth(); }}
            className="p-1 rounded hover:bg-background/50 cursor-pointer"
            title="Refresh status"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); checkHealth(); }}}
          >
            <RefreshCw className="w-3 h-3 text-muted-foreground" />
          </span>
        )}
      </button>

      {/* Expanded Details */}
      {showDetails && details && (
        <div className={`p-3 rounded-xl ${config.bgColor} border ${config.borderColor} space-y-2 text-xs`}>
          {/* API Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground">API Server</span>
            </div>
            <div className="flex items-center gap-2">
              {details.checks?.api?.status === 'healthy' ? (
                <span className="text-green-500">OK</span>
              ) : (
                <span className="text-red-500">Error</span>
              )}
              {details.checks?.api?.latency_ms && (
                <span className="text-muted-foreground">{details.checks.api.latency_ms}ms</span>
              )}
            </div>
          </div>

          {/* Database Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground">Database</span>
            </div>
            <div className="flex items-center gap-2">
              {details.checks?.database?.status === 'healthy' ? (
                <span className="text-green-500">OK</span>
              ) : (
                <span className="text-red-500">Error</span>
              )}
              {details.checks?.database?.latency_ms && (
                <span className="text-muted-foreground">{details.checks.database.latency_ms}ms</span>
              )}
            </div>
          </div>

          {/* Client Latency */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wifi className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground">Round Trip</span>
            </div>
            <span className={details.clientLatency > 500 ? 'text-amber-500' : 'text-muted-foreground'}>
              {details.clientLatency ? `${details.clientLatency}ms` : '-'}
            </span>
          </div>

          {/* Error Message */}
          {details.error && (
            <div className="pt-2 border-t border-border/50">
              <p className="text-red-400 text-[10px]">{details.error}</p>
            </div>
          )}

          {/* Last Check Time */}
          {lastCheck && (
            <div className="pt-2 border-t border-border/50 text-[10px] text-muted-foreground">
              Last checked: {lastCheck.toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
